import React, { FC, useState } from 'react'

import { useForm, useFieldArray } from 'react-hook-form'
import { v4 as uuidV4 } from 'uuid'

import { DecorationTitle } from 'src/components/atoms'
import { SettingButtons } from 'src/components/molecules'
import { InternalUserDetailBasicInfoContent } from 'src/components/organisms'

import { BaseYup, yup, yupResolver } from 'src/customYup'
import {
  useAuthority,
  useGetProfile,
  useGetUser,
  useGetUsers,
  useMasterData,
  useToast,
  useUser,
} from 'src/hooks'
import { isArraysAreEqual } from 'src/libs/helper'
import { EditUserBasicInfoFormEntity, ProfileEntity, InternalUserDetailEntity } from 'src/types'

const schema: yup.SchemaOf<EditUserBasicInfoFormEntity> = BaseYup.object({
  name: BaseYup.string(),
  keywords: BaseYup.array()
    .of(
      BaseYup.object({
        id: BaseYup.string().required(),
        value: BaseYup.string().required(),
      })
    )
    .optional(),
})

type Props = {
  user: InternalUserDetailEntity | ProfileEntity
  className?: string
}

export const InternalUserDetailBasicInfo: FC<Props> = ({ user, className }) => {
  const [isEditMode, setIsEditMode] = useState(false)
  const [isUpdating, setIsUpdating] = useState(false)

  const toDefaultValues = {
    name: user.name ?? undefined,
    keywords:
      user.keywords && user.keywords.length > 0
        ? user.keywords.map(keyword => ({ id: uuidV4(), value: keyword }))
        : [{ id: uuidV4(), value: '' }],
  }

  const { control, register, watch, setValue, reset } = useForm<EditUserBasicInfoFormEntity>({
    resolver: yupResolver(schema),
    defaultValues: toDefaultValues,
  })
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'keywords',
  })

  const { masterData } = useMasterData()
  const { updateName, updateKeywords } = useUser()
  const { displayStandardTextToast } = useToast()
  const { refetch: refetchUser } = useGetUser({ userId: user.id })
  const { refetch: refetchUsers } = useGetUsers()
  const { profile, refetch: refetchProfile } = useGetProfile()
  const { canManageUser, isSignInUser } = useAuthority()

  const handleFinishEdit = async () => {
    setIsUpdating(true)

    try {
      let updateFuncs: Array<Promise<any>> = []

      if (!!watch('name') && user.name !== watch('name')) {
        // プロフィール更新、更新後の名前がない事はあり得ないはずだが、存在しない場合は以前の名前、空文字の順で設定する
        const f = updateName({ userId: user.id, updatedName: watch('name') ?? user.name ?? '' })
        updateFuncs = [f]
      }

      const beforeKeywords = user.keywords ?? []
      const updatedKeywords = watch('keywords')?.map(keyword => keyword.value) ?? []
      // 変更前、変更後のキーワードが異なる場合のみ更新する
      if (!isArraysAreEqual(beforeKeywords, updatedKeywords)) {
        const f = updateKeywords({ userId: user.id, updatedKeywords })
        updateFuncs = [...updateFuncs, f]
      }

      if (updateFuncs.length > 0) {
        await Promise.all(updateFuncs)
        // 名前・ロール更新後、ユーザー詳細・ユーザー一覧・プロフィールも更新する
        await Promise.all([refetchUser(), refetchUsers(), refetchProfile()])
        displayStandardTextToast('変更しました')
      }
    } catch (error) {
      console.log('e: ', error)
    } finally {
      setIsEditMode(false)
      setIsUpdating(false)
    }
  }

  const enabledCheckButton =
    (!!watch('name') || watch('keywords')?.some(keyword => !!keyword.value)) ?? false

  // 設定ページを押下した際にreact-hook-form内のキーワードが0件の場合は1件ある状態にする
  const handleOnClickSetting = () => {
    const watchedKeywords = watch('keywords')
    if (watchedKeywords && watchedKeywords.length === 0) {
      append({ id: uuidV4(), value: '' })
    }
  }

  return (
    <div className={className}>
      <div className="tw-full-justify">
        <DecorationTitle>プロフィール</DecorationTitle>
        {(canManageUser || isSignInUser({ userId: user.id })) && (
          <SettingButtons
            isEditMode={isEditMode}
            isLoading={isUpdating}
            updateIsEditMode={setIsEditMode}
            enabledCheckButton={enabledCheckButton}
            finishEdit={handleFinishEdit}
            onClickCancel={() => reset(toDefaultValues)}
            onClickSetting={handleOnClickSetting}
          />
        )}
      </div>
      {profile && (
        <InternalUserDetailBasicInfoContent
          user={user}
          isEditMode={isEditMode}
          isUpdating={isUpdating}
          register={register}
          watch={watch}
          setValue={setValue}
          fields={fields}
          append={append}
          remove={remove}
          worksRoles={masterData?.worksRoles ?? []}
          className="tw-mt-s2"
        />
      )}
    </div>
  )
}
